import {
  Button,
  Code,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
} from "@chakra-ui/react";
import { Anchor } from "@mantine/core";
import { NextPage } from "next";
import { useRouter } from "next/router";
import Page from "../components/Page";
import { WithSideNavigation } from "../components/WithSideNavigation/WithSideNavigation";
import {
  FieldColoring,
  SystemOfMeasurement,
  fieldColorings,
  systemsOfMeasurement,
  useFieldColoring,
  useSystemOfMeasurement,
} from "../hooks/storage/storageHooks";
import {
  USER_TYPE_ADMIN,
  USER_TYPE_USER,
  useApiKey,
  useEmail,
  useEnterpriseId,
  useUserId,
  useUserType,
  useUsername,
  useUsernameAuthGuard,
} from "../hooks/useAuthGuard";
import { useLocale } from "../hooks/useLocale";
import { useSession } from "../hooks/useSession";
import { useTranslation } from "../i18n/useTranslation";
import { fullSignOut } from "../utils/fullSignOut";

const ProfilePage: NextPage = () => {
  useUsernameAuthGuard();

  const router = useRouter();
  const { locale } = useLocale();
  const { t } = useTranslation();

  const [systemOfMeasurement, setSystemOfMeasurement] =
    useSystemOfMeasurement();
  const [fieldColoring, setFieldColoring] = useFieldColoring();

  const userEmail = useEmail();

  const userId = useUserId();
  const userType = useUserType();
  const username = useUsername();
  const enterpriseId = useEnterpriseId();
  const apiKey = useApiKey();
  const session = useSession();

  return (
    <Page title="Profile | KARL" description="Profile">
      <WithSideNavigation>
        <Flex
          direction="column"
          padding="50px"
          gap="50px"
          alignItems="flex-start"
          overflowY="auto"
        >
          <Flex direction="column" gap="10px">
            <Heading as="h1" size="lg">
              {t("page.profile.heading.generalInformation")}
            </Heading>

            <Flex direction="column" gap="10px" alignItems="flex-start">
              <Text>
                <strong>{t("page.profile.userEmail")}:</strong>{" "}
                {userEmail || "n/a"}
              </Text>
              {userId ? (
                <Flex gap="10px">
                  <Button onClick={fullSignOut}>
                    {t("page.profile.logOut")}
                  </Button>
                </Flex>
              ) : null}
            </Flex>
          </Flex>

          <Flex direction="column" gap="20px">
            <Heading as="h1" size="lg">
              {t("page.profile.heading.settings")}
            </Heading>
            <FormControl>
              <FormLabel>{t("page.profile.language")}</FormLabel>
              <Select
                onChange={(e) => {
                  router.push(router.asPath, undefined, {
                    locale: e.target.value,
                  });
                }}
                value={locale}
              >
                <option value="en">{"English"}</option>
                <option value="da">{"Dansk"}</option>
                <option value="es">{"Español"}</option>
                <option value="fr">{"Français"}</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>
                {t("page.profile.label.systemOfMeasurement")}
              </FormLabel>
              <Select
                onChange={(e) => {
                  setSystemOfMeasurement(e.target.value as SystemOfMeasurement);
                }}
                value={systemOfMeasurement}
              >
                {systemsOfMeasurement.map((system) => (
                  <option key={system} value={system}>
                    {t(`page.profile.systemOfMeasurement.${system}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>{t("page.profile.label.fieldColoring")}</FormLabel>
              <Select
                onChange={(e) => {
                  setFieldColoring(e.target.value as FieldColoring);
                }}
                value={fieldColoring}
              >
                {fieldColorings.map((coloring) => (
                  <option key={coloring} value={coloring}>
                    {t(`page.profile.fieldColoring.${coloring}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Flex>

          <Flex direction="column" gap="20px">
            <Heading as="h1" size="lg">
              {t("page.profile.heading.feedback")}
            </Heading>
            <Flex direction="column" gap="10px">
              <Text>{t("page.profile.feedback.description")}</Text>
              <Anchor href="https://karl.farm/company/contact" target="_blank">
                {t("page.profile.feedback.button.submit")}
              </Anchor>
            </Flex>
          </Flex>

          <Flex direction="column" gap="20px">
            <Heading as="h1" size="lg">
              {t("page.profile.heading.legal")}
            </Heading>
            <Flex direction="column" gap="10px">
              <Anchor href="https://karl.farm/legal#terms" target="_blank">
                {t("page.profile.legal.termsAndConditions")}
              </Anchor>
              <Anchor href="https://karl.farm/legal#privacy" target="_blank">
                {t("page.profile.legal.privacyPolicy")}
              </Anchor>
            </Flex>
          </Flex>

          {process.env.NODE_ENV === "development" ? (
            <Flex direction="column" gap="10px">
              <Heading as="h1" size="lg">
                Debug info
              </Heading>
              <Text>
                <strong>User ID:</strong> {userId || "n/a"}
              </Text>
              <Text>
                <strong>User type:</strong> {userType || "n/a"}
                {userType === USER_TYPE_ADMIN
                  ? " (admin)"
                  : userType === USER_TYPE_USER
                  ? " (not admin)"
                  : null}
              </Text>
              <Text>
                <strong>Username:</strong> {username || "n/a"}
              </Text>
              <Text>
                <strong>Enterprise ID:</strong> {enterpriseId || "n/a"}
              </Text>
              <Text>
                <strong>API key:</strong> {apiKey || "n/a"}
              </Text>
              <Text>
                <strong>Session:</strong>
              </Text>
              <Code>
                <pre>{JSON.stringify(session, null, 2)}</pre>
              </Code>
            </Flex>
          ) : null}
        </Flex>
      </WithSideNavigation>
    </Page>
  );
};

export default ProfilePage;
